import { apiClient } from "@/lib/api";
import type {
  FloorplanSummary,
  Invoice,
  InvoiceCreateBody,
  InvoicePayment,
  InvoiceUpdateBody,
  MonthlyTotal,
  PaymentRecordBody,
  PnL,
  StorageInvoicingStatus,
  Transaction,
  TransactionFilters,
  TransactionWriteBody,
} from "@/types/finance";

export type { TransactionFilters, TransactionWriteBody };
export type { InvoiceCreateBody, InvoiceUpdateBody, PaymentRecordBody };

export const fetchPnL = async (from: string, to: string): Promise<PnL> =>
  apiClient<PnL>("/finance/pnl", { params: { from, to } });

export const fetchTrend = async (months = 12): Promise<MonthlyTotal[]> =>
  apiClient<MonthlyTotal[]>("/finance/trend", { params: { months } });

// Aggregate floor-plan exposure (active loans, accrued curtailments, paid
// totals) plus one row per active loan. The read also refreshes the loans'
// schedules server-side, so it stays current without waiting for the daily
// scheduler pass.
export const fetchFloorplanSummary = async (): Promise<FloorplanSummary> =>
  apiClient<FloorplanSummary>("/finance/floorplan-summary");

export const fetchTransactions = async (filters: TransactionFilters): Promise<Transaction[]> => {
  const params: Record<string, string> = {};
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.type) params.type = filters.type;
  if (filters.category) params.category = filters.category;
  if (filters.q) params.q = filters.q;
  return apiClient<Transaction[]>("/finance/transactions", {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
};

export const createTransaction = async (body: TransactionWriteBody) =>
  apiClient<Transaction>("/finance/transactions", { method: "POST", body });

export const updateTransaction = async (id: string, body: TransactionWriteBody) =>
  apiClient<Transaction>(`/finance/transactions/${id}`, { method: "PUT", body });

export const deleteTransaction = async (id: string) =>
  apiClient<void>(`/finance/transactions/${id}`, { method: "DELETE" });

// ---- Accounts receivable: invoices + payments ----

export const fetchInvoices = (filters: {
  status?: string
  source?: string
  customer_id?: string
  q?: string
}) => {
  const params: Record<string, string> = {}
  if (filters.status) params.status = filters.status
  if (filters.source) params.source = filters.source
  if (filters.customer_id) params.customer_id = filters.customer_id
  if (filters.q) params.q = filters.q
  return apiClient<Invoice[]>("/finance/invoices", { params });
};

export const fetchInvoice = (id: string) =>
  apiClient<Invoice>(`/finance/invoices/${id}`);

export const createInvoice = (body: InvoiceCreateBody) =>
  apiClient<Invoice>("/finance/invoices", { method: "POST", body });

export const updateInvoiceDraft = (id: string, body: InvoiceUpdateBody) =>
  apiClient<Invoice>(`/finance/invoices/${id}`, { method: "PUT", body });

export const openInvoice = (id: string) =>
  apiClient<Invoice>(`/finance/invoices/${id}/open`, { method: "POST" });

export const voidInvoice = (id: string) =>
  apiClient<Invoice>(`/finance/invoices/${id}/void`, { method: "POST" });

export const recordPayment = (id: string, body: PaymentRecordBody) =>
  apiClient<{ payment: InvoicePayment; invoice: Invoice }>(
    `/finance/invoices/${id}/payments`,
    { method: "POST", body },
  );

export const fetchStorageInvoicingStatus = () =>
  apiClient<StorageInvoicingStatus>("/finance/invoices/storage/status");
